import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { IonIcon, IonTabBar, IonTabs, ModalController, NavController } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';

import {
  BottomSheetDirective,
  CompanySelectorMenuComponent,
  UserMenuComponent,
} from '@ninety/mobile/platform/components';
import {
  APP_ROUTES,
  extractValueFromStore,
  GlobalNavActions,
  RouterSelectors,
  selectActiveGlobalNavKey,
  selectGlobalMoreItems,
  selectGlobalNavItems,
  selectIsAtTabLanding,
  UniversalCreateActions,
} from '@ninety/mobile/platform/state';

/**
 * The global navigation: the bottom tab bar and the three menus, which stay on screen while the
 * screen below them changes. It is the routed component at the access boundary, so the route guards
 * have already run and the menus need no condition of their own.
 *
 * `ion-tabs` is the top element. It used to sit inside an `ion-content` wrapping the whole
 * application, which on iOS put the tab bar inside a scroll container — a touch that starts there
 * and moves becomes a scroll, and the browser cancels the click the tab button acts on.
 */
@Component({
  selector: 'nty-authenticated-layout',
  imports: [
    LetDirective,
    IonTabs,
    IonTabBar,
    IonIcon,
    CompanySelectorMenuComponent,
    UserMenuComponent,
    BottomSheetDirective,
    AsyncPipe,
  ],
  template: `
    <!-- No router outlet here: ion-tabs implements its own. -->

    @let moreItems = more$ | async;

    <ion-tabs [id]="contentId" *ngrxLet="vm$ as vm">
      <ion-tab-bar slot="bottom" [class.hide]="vm.hideTabs">
        @for (menuItem of vm.menu; track menuItem.key) {
          @let selected = vm.activeKey === menuItem.key;
          <button
            type="button"
            [id]="'tab-button-' + menuItem.key"
            class="terra-body-small nav-btn"
            [class.selected]="selected"
            [attr.aria-current]="selected ? 'page' : null"
            (click)="onNavItemTapped(menuItem.key, selected)">
            <ion-icon [src]="selected ? menuItem.iconFill : menuItem.iconRegular" />
            <span class="nav-label">{{ menuItem.label }}</span>
          </button>
        }
        <!-- Need to check if there are moreItems, with tiered access it's not guaranteed -->
        <button type="button" class="terra-body-small nav-btn" (click)="openUC()" aria-label="Open universal create">
          <ion-icon src="/assets/icons/add--regular.svg" />
          <span class="nav-label">Create</span>
        </button>
        @if (moreItems && moreItems.length > 0) {
          <button
            type="button"
            class="terra-body-small nav-btn"
            aria-label="Open more menu"
            [ntyBottomSheet]="moreBottomSheet"
            [modalCss]="['more-sheet']"
            [modalId]="'nty-more-sheet'"
            [handle]="false">
            <ion-icon src="/assets/icons/more--regular.svg" />
            <span class="nav-label">More</span>
          </button>
        }
      </ion-tab-bar>
    </ion-tabs>

    <ng-template #moreBottomSheet>
      <h2 class="terra-headline-3 title">More</h2>

      <div class="more-sheet-btns">
        @for (moreItem of moreItems; track moreItem.key) {
          <button type="button" class="terra-title-light more-item" (click)="onMoreItemTapped(moreItem.key)">
            <ion-icon [src]="moreItem.iconRegular" />
            <span>{{ moreItem.label }}</span>
          </button>
        }
      </div>
    </ng-template>

    <nty-company-selector-menu [contentId]="contentId" />
    <nty-user-menu [contentId]="contentId" />
  `,
  styles: [
    `
      @use 'terra';

      ion-tabs {
        background: terra.color(background, heavy);
      }

      ion-tab-bar {
        --background: #{terra.color(background)};
        --color-selected: #{terra.color(ninety)};
        border-top: none;
        @include terra.elevation(2, up);

        padding-top: 6px;
        padding-inline: 24px;
        // For viewing on web vs non-native apps we need to have some padding-bottom
        // For native we'll use the ionic safe-areas (below in ng-deep) which is needed on native apps
        padding-bottom: 10px;

        &.hide {
          display: none;
        }
      }

      // Create and More are plain buttons rather than ion-tab-button: they open a modal and a
      // sheet instead of selecting a tab, and ion-tab-button renders its native element as an
      // <a>, which on iOS drops ~25% of synthesized clicks and delays the rest ~144ms while the
      // native analytics SDKs are resident. Measured on device; both faults vanish on a <button>.
      // These rules restore the layout ion-tab-button was providing.
      .nav-btn {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        padding: 0;
        background: transparent;
        border: none;
        font: inherit;
        color: terra.color(text);

        ion-icon {
          font-size: 24px;
        }

        &:active,
        &.selected {
          color: terra.color(ninety);
        }
      }

      .nav-label {
        display: inline-block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 60px;
      }

      // Ng-deep needed to target these platform selectors on html element
      // https://github.com/ionic-team/ionic-framework/blob/4cbbbb053ad36d176f1d79ad09777f94ca8076d2/BREAKING_ARCHIVE/v4.md?plain=1#L1495
      ::ng-deep {
        .plt-mobile:not(.plt-mobileweb):is(.plt-ios, .plt-android) {
          ion-tab-bar {
            padding-bottom: var(--ion-safe-area-bottom);
          }
        }
      }

      ::ng-deep {
        ion-modal.more-sheet {
          --height: auto;

          .title {
            padding-block: 8px;
            // Without this the heading keeps the modal's default, which is unreadable in dark mode.
            color: terra.color(text);
          }

          // Keeps the last item clear of the Android system nav bar (same inset as ion-tab-bar).
          .more-sheet-btns {
            padding-bottom: var(--ion-safe-area-bottom);
          }

          // Plain buttons, not ion-button + routerLink (an <a> on iOS; see .nav-btn above).
          .more-item {
            display: flex;
            align-items: center;
            width: 100%;
            // Size and spacing of the ion-button these replaced, as rendered on iOS (measured).
            font-size: 18px;
            min-height: 3.1em;
            margin-block: 4px;
            padding-inline: 6px;
            background: transparent;
            border: none;
            text-align: start;
            color: terra.color(text);

            ion-icon {
              font-size: 1.35em;
              margin-inline: -0.3em 0.3em;
            }

            &:active {
              color: terra.color(ninety);
            }
          }
        }
      }
    `,
  ],
})
export class AuthenticatedLayoutComponent {
  private modal = inject(ModalController);
  private navCtrl = inject(NavController);

  protected appRouteUrlPrefix = `/${APP_ROUTES.App}/`;

  /** The Ionic menus find their content by id; ion-tabs serves as well as anything else. */
  contentId = 'nty-auth-layout';

  store = inject(Store);

  protected more$ = this.store.select(selectGlobalMoreItems);

  vm$ = {
    menu: this.store.select(selectGlobalNavItems),

    activeKey: this.store.select(selectActiveGlobalNavKey),

    hideTabs: this.store.select(RouterSelectors.selectHideTabNavigation),
  };

  /**
   * Tab navigation runs off the click.
   *
   * The bar's buttons are plain `<button>`s rather than `ion-tab-button`, which renders its native
   * element as an `<a>`. Measured on device, taps on that anchor lose ~25% of their synthesized
   * clicks and the survivors arrive ~144ms late while the native analytics SDKs are resident; a
   * plain button in the same bar drops none and lands in ~16ms. So the click is dependable here and
   * needs no pointer-event workaround.
   *
   * Navigation goes through `NavController.navigateRoot` directly rather than `IonTabs.select()`.
   * `select()` only resets a tab's own view stack to its root when that tab is already active; for
   * any other tab it resumes whatever page was last left open there (Ionic's built-in per-tab
   * stack memory). That leaves a tab stranded on a stale detail page — e.g. one left behind by an
   * abandoned chain of linked-item drill-downs — instead of landing on its list, which is what a
   * tap on a *different* main-nav tab should always do here. `navigateRoot` resets the target
   * tab's stack the same way `select()`'s own already-active branch does, just unconditionally.
   */
  protected onNavItemTapped(tab: string, wasSelected: boolean): void {
    this.store.dispatch(GlobalNavActions.navItemTapped({ featureKey: tab, wasSelected }));

    // Nothing to do when the tab already shows the screen the tap would navigate to.
    if (extractValueFromStore(this.store, selectIsAtTabLanding(tab))) return;

    void this.navCtrl.navigateRoot(`${this.appRouteUrlPrefix}${tab}`, {
      animated: true,
      animationDirection: 'back',
    });
  }

  /** Navigates off the click, like the tab bar, then closes the sheet. */
  protected async onMoreItemTapped(key: string): Promise<void> {
    void this.navCtrl.navigateForward(`${this.appRouteUrlPrefix}${key}`);
    await this.modal.dismiss(null, '', 'nty-more-sheet');
  }

  protected openUC() {
    this.store.dispatch(UniversalCreateActions.open({ dispatchSource: 'PageComponent' }));
  }
}
